import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, Search } from 'lucide-react';

interface SchoolRow {
  kvCode: number | string;
  schoolName: string;
  state: string;
  district: string;
  totalRegistered: number;
  totalAppeared: number;
  totalPassed: number;
  totalCompartment: number;
  totalAbsent: number;
  totalEssentialRepeat: number;
  passPercentage: number;
  performanceIndex: number;
}

type SortKey = keyof SchoolRow;

interface Props {
  data: SchoolRow[];
  activeTab: string;
  year: number;
}

const SchoolWiseTable: React.FC<Props> = ({ data, activeTab, year }) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('passPercentage');
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState('');

  const avgPassPct = useMemo(
    () => (data.length ? data.reduce((s, r) => s + r.passPercentage, 0) / data.length : 0),
    [data],
  );
  const avgPerfIndex = useMemo(
    () => (data.length ? data.reduce((s, r) => s + r.performanceIndex, 0) / data.length : 0),
    [data],
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const filtered = data
    .filter((r) =>
      r.schoolName.toLowerCase().includes(search.toLowerCase()) ||
      String(r.kvCode).includes(search),
    )
    .sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  const ThBtn: React.FC<{ label: string; field: SortKey }> = ({ label, field }) => (
    <th
      className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === field ? 'text-purple-600' : 'text-gray-400'}`} />
      </span>
    </th>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <h3 className="text-lg font-semibold text-gray-800">Schools — Drill Down</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{data.length} schools</span>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search school or KV code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
          </div>
        </div>
      </div>

      <div className="overflow-auto max-h-[560px]">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">#</th>
              <ThBtn label="KV Code" field="kvCode" />
              <ThBtn label="School Name" field="schoolName" />
              <ThBtn label="Registered" field="totalRegistered" />
              <ThBtn label="Absent" field="totalAbsent" />
              <ThBtn label="Appeared" field="totalAppeared" />
              <ThBtn label="Passed" field="totalPassed" />
              <ThBtn label="Compartment" field="totalCompartment" />
              <ThBtn label="Ess. Repeat" field="totalEssentialRepeat" />
              <ThBtn label={`Pass % (avg ${avgPassPct.toFixed(1)}%)`} field="passPercentage" />
              <ThBtn label={`Perf. Index (avg ${avgPerfIndex.toFixed(2)})`} field="performanceIndex" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((row, i) => (
              <tr
                key={String(row.kvCode)}
                className="hover:bg-orange-50 transition-colors cursor-pointer"
                onClick={() => navigate(`school/${row.kvCode}?class=${activeTab}&year=${year}`)}
              >
                <td className="py-3 px-4 text-sm text-gray-500">{i + 1}</td>
                <td className="py-3 px-4 text-sm font-mono text-gray-600">{row.kvCode}</td>
                <td className="py-3 px-4 text-sm font-medium text-gray-800">{row.schoolName}</td>
                <td className="py-3 px-4 text-sm text-gray-700">{(row.totalRegistered ?? 0).toLocaleString()}</td>
                <td className="py-3 px-4 text-sm text-blue-600">{row.totalAbsent ?? 0}</td>
                <td className="py-3 px-4 text-sm text-gray-700">{(row.totalAppeared ?? 0).toLocaleString()}</td>
                <td className="py-3 px-4 text-sm text-green-700 font-medium">{(row.totalPassed ?? 0).toLocaleString()}</td>
                <td className="py-3 px-4 text-sm text-orange-600">{row.totalCompartment ?? 0}</td>
                <td className="py-3 px-4 text-sm text-red-600">{row.totalEssentialRepeat ?? 0}</td>
                <td className="py-3 px-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    row.passPercentage >= avgPassPct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {row.passPercentage}%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm font-medium">
                  <span className={row.performanceIndex >= avgPerfIndex ? 'text-green-700' : 'text-red-600'}>
                    {row.performanceIndex}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={12} className="py-8 text-center text-gray-400">No schools found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SchoolWiseTable;
